/**
 * Acceptable Use Policy Page
 *
 * Part of the Terms of Service by incorporation (TOS §1.1, §1.6, §1.14).
 * Enforcement follows TOS §1.7 (moderation tiers, strikes, appeals).
 *
 * Section numbers (5.x) are stable anchors; never renumber.
 *
 * v1 (October 2026): initial policy. Created alongside TOS v5 because the
 * Terms referenced an Acceptable Use Policy that did not yet exist.
 *
 * Place at: src/pages/AcceptableUsePage.jsx
 * Route:    <Route path="/acceptable-use" element={<AcceptableUsePage />} />
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  PageShell, Section, SubHead, BulletList, LINK,
} from '../components/legal/LegalLayout';

const AUP_VERSION = 1;
const EFFECTIVE_DATE = 'October 17, 2026'; // TODO: match TermsPage EFFECTIVE_DATE

const Mail = () => <a href="mailto:support@enume.io" className={LINK}>support@enume.io</a>;

const AcceptableUsePage = () => (
  <PageShell current="/acceptable-use">
    <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 tracking-tight">
      Acceptable Use Policy
    </h1>
    <p className="text-zinc-500 text-sm mb-10">Effective {EFFECTIVE_DATE} · Version {AUP_VERSION}</p>

    <div className="space-y-8">

      <Section id="5.1" title="Purpose & Scope">
        <p>Enume exists so people can build on each other's work across disciplines. This policy describes what that requires of everyone. It is part of the <Link to="/terms" className={LINK}>Terms of Service</Link> and applies to all content and conduct on the Service — projects, forks, challenges, comments, Uses, collections, messages, cohorts, profiles, and API use.</p>
        <p>The examples below are not exhaustive. Enume may act on conduct that violates the spirit of this policy even if it is not listed. Capitalized terms have the meanings given in the Terms.</p>
      </Section>

      <Section id="5.2" title="Illegal and Dangerous Content" accent="amber">
        <p>Never post, share, or request:</p>
        <BulletList items={[
          'Any sexual or sexualized content involving a minor, or anyone who appears to be a minor. Enume has zero tolerance: the account is terminated and the content is reported to the National Center for Missing & Exploited Children as required by law',
          'Intimate images of anyone shared without their consent, including altered or AI-generated depictions of real people',
          'Content that promotes, supports, or recruits for terrorism or violent extremism',
          'Credible threats of violence, or content that incites violence against people or groups',
          'Instructions or materials intended to help someone create weapons capable of mass harm, or to commit a crime',
          'Offers to sell or trade illegal goods or services, including controlled substances and weapons',
          'Content that promotes or instructs self-harm or suicide (discussion aimed at support and recovery is allowed)',
          'Anything else that is illegal where you are or in California',
        ]} />
      </Section>

      <Section id="5.3" title="Harassment, Hate & Safety">
        <BulletList items={[
          'Do not harass, bully, intimidate, or stalk anyone, on or off the Service',
          'Do not attack people based on race, ethnicity, national origin, religion, caste, sex, gender, gender identity, sexual orientation, disability, age, or serious illness',
          'Do not dox: post someone\'s private information (home address, phone number, private email, financial details, identity documents, location) without their consent',
          'Do not encourage others to target a person, including through coordinated reporting, forking, or commenting',
          'Do not use forks, challenge responses, or Uses to mock, demean, or harass the original creator',
        ]} />
      </Section>

      <Section id="5.4" title="Privacy of Others">
        <BulletList items={[
          'Get consent before posting recordings, photos, or likenesses of identifiable people where the law requires it — California requires the consent of all parties to record a confidential conversation (Penal Code § 632)',
          'Do not use the Service, its API, or cohort tools to collect other users\' personal information for purposes they have not agreed to',
          'Do not republish someone\'s Private Communications without their permission',
        ]} />
      </Section>

      <Section id="5.5" title="Intellectual Property & Licensing">
        <p>Because everything posted publicly is licensed under CC BY-SA 4.0 (Terms §1.3), only post what you have the right to license that way.</p>
        <BulletList items={[
          'Do not post work you didn\'t create unless it is in the public domain or licensed in a way compatible with CC BY-SA 4.0, and credit it properly',
          'Do not remove, alter, or obscure attribution, license notices, or lineage — including by re-uploading someone\'s work as a new, unconnected project instead of forking it',
          'Do not use trademarks or brand assets in a way that suggests endorsement or confuses people about who made something',
          <>Copyright complaints follow the <Link to="/dmca" className={LINK}>DMCA & Copyright Policy</Link></>,
        ]} />
      </Section>

      <Section id="5.6" title="Academic Integrity">
        <p>Do not upload exam questions, test banks, answer keys, solution manuals, proprietary course materials, or anything that facilitates cheating. The full rules are in <Link to="/terms#1.6A" className={LINK}>Terms §1.6A</Link>.</p>
      </Section>

      <Section id="5.7" title="Mature Content">
        <BulletList items={[
          'Rate content accurately using the Service\'s rating system (Safe, Teen, Mature, Explicit). Deliberately mis-rating content is a violation',
          'Do not use mature or explicit content as a cover image, profile image, or in any place where ratings cannot be applied',
          'Do not post sexual content in cohorts, challenges, or comment threads not clearly designated for it',
          'Content depicting real, identifiable people in a sexual context requires their documented consent',
        ]} />
      </Section>

      <Section id="5.8" title="Honesty & Platform Integrity">
        <BulletList items={[
          'Do not impersonate any person, organization, or Enume staff',
          'Do not misrepresent your age, role, institutional affiliation, or authority in a cohort',
          'Do not create fake accounts, operate multiple accounts to evade enforcement, or sell or share accounts',
          'Do not manipulate forks, lineage, views, stars, statistics, or rankings — including fake forks, sockpuppet challenge responses, and automated engagement',
          'Do not post spam, repetitive content, or unsolicited advertising, including in messages',
          'Do not pass off someone else\'s work as your own',
          'Do not file false or bad-faith reports or appeals',
        ]} />
      </Section>

      <Section id="5.9" title="Security & Technical Abuse">
        <BulletList items={[
          'Do not access, or try to access, accounts, data, or systems you are not authorized to use',
          'Do not upload malware, viruses, or files designed to exploit software or devices',
          'Do not scrape, crawl, or bulk-download the Service except through the authorized API within its limits',
          'Do not circumvent rate limits, access controls, content filters, bans, or suspensions',
          'Do not overload, disrupt, or interfere with the Service or other users\' use of it',
          'Do not share API keys, or use a key beyond its granted scope',
        ]} />
        <p className="text-zinc-500 text-xs">Found a security vulnerability? Report it privately to <Mail /> and give us reasonable time to fix it before disclosing it. Good-faith reports that avoid privacy violations, data destruction, and service disruption will not be treated as violations of this section.</p>
      </Section>

      <Section id="5.10" title="Marketplace & Payments">
        <BulletList items={[
          'Do not commit payment fraud, use stolen payment methods, or abuse chargebacks',
          'Do not create fake projects, sales, or lineage connections to generate revenue or revenue-share',
          'Do not sell items that violate this policy or that you do not have the right to sell',
          'Describe items for sale accurately',
        ]} />
      </Section>

      <Section id="5.11" title="Cohorts & Instructors">
        <BulletList items={[
          'Do not invite, enroll, or direct anyone under 18 to use the Service (Terms §1.2)',
          'Do not require students to post content they cannot license under CC BY-SA 4.0',
          'Do not use cohort tools to collect personal information beyond what the Service displays',
          'Use cohort labels and organization respectfully; do not use them to shame or single out members',
        ]} />
      </Section>

      <Section id="5.12" title="Reporting & Enforcement">
        <p>Report content or conduct using the report tools on the Service, or by emailing <Mail />. In an emergency, contact local authorities first.</p>
        <p>Violations are handled under <Link to="/terms#1.7" className={LINK}>Terms §1.7</Link>: content may be flagged, hidden, or removed; accounts may receive strikes, be suspended, or be terminated; and you may appeal. Enume may also report illegal activity to law enforcement and preserve information as required by law.</p>
      </Section>

      <Section id="5.13" title="Changes to This Policy">
        <p>This policy may change as the Service changes. Material changes follow the notice rules in <Link to="/terms#1.14B" className={LINK}>Terms §1.14B</Link>.</p>
      </Section>

      <p className="text-zinc-500 text-xs pt-4">
        Questions about this policy: <Mail />
      </p>
    </div>
  </PageShell>
);

export default AcceptableUsePage;
